<template>
  <div class="furikyuu">
    <h3>振休・振出申請</h3>
    <div class="message-area">
      <!-- 振休申請時のみ振休残を表示する。振出申請は振休を増やす側のため表示不要 -->
      <div class="message info" v-if="furikyuuCount && furikyuuCount.zan > 0 && isFurikyuu">
        <p>
          振休残が{{ furikyuuCount.zan }}日あります
          <template v-if="furikyuuCount.zan < 0">
            <br><span style="color: red;">※振出日数が{{ furikyuuCount.zan * -1 }}日不足しています</span>
          </template>
        </p>
      </div>
    </div>

    <template v-if="form.furikyuu">
      <div class="item">
        <b class="title">種別</b>
        <input-radio :options="options" v-model="isFurikyuu" @change="clear"></input-radio>
      </div>

      <template v-if="!isFurikyuu">
        <div class="item">
          <b class="title">出勤日</b>
          <date-picker v-model="form.furikyuu.syukkin_date"
            :error="getError(form.furikyuu, 'syukkin_date')"></date-picker>
        </div>

        <div class="item">
          <koyouKeiyakuTimeSelect v-if="isInit" :hideWorkTime=true :errors="form.errors"
            :targetDate="form.furikyuu.syukkin_date" :koyouKeiyakuId="koyouKeiyakuId" :workTimeId="workTimeId"
            @change="changeKoyouKeiyakuTime">
          </koyouKeiyakuTimeSelect>
        </div>

        <div class="item">
          <b class="title">振出取得区分</b>
          <select-box :options="furideDiv" v-model="form.furikyuu.syukkin_furidetani_type" :hasBlank="true"
            :error="getError(form.furikyuu, 'syukkin_furidetani_type')"></select-box>
        </div>
        <div class="item">
          <b class="title">出勤時間</b>
          <time-picker v-model="form.furikyuu.start_time" :startTime="{ minutes: 0 }"
            :error="getError(form.furikyuu, 'start_time')"></time-picker>
        </div>
        <div class="item">
          <b class="title">出勤時間終了</b>
          <time-picker v-model="form.furikyuu.end_time" :startTime="{ minutes: 0 }"
            :error="getError(form.furikyuu, 'end_time')"></time-picker>
        </div>
        <div class="item">
          <b class="title">休憩時間</b>
          <time-picker v-model="form.furikyuu.kyuukei_time" :startTime="{ hours: 0, minutes: 0 }"
            :error="getError(form.furikyuu, 'kyuukei_time')" :minTime="{ hours: 0, minutes: 0, seconds: 0 }"
            :maxTime="{ hours: 2, minutes: 0, seconds: 0 }"></time-picker>
        </div>
      </template>

      <template v-else>
        <div class="item">
          <b class="title">振休取得日</b>
          <date-picker v-model="form.furikyuu.furikyuu1_date"
            :error="getError(form.furikyuu, 'furikyuu1_date')"></date-picker>
        </div>

        <div class="item">
          <koyouKeiyakuTimeSelect v-if="isInit" :hideWorkTime=true :errors="form.errors"
            :targetDate="form.furikyuu.furikyuu1_date" :koyouKeiyakuId="koyouKeiyakuId" :workTimeId="workTimeId"
            @change="changeKoyouKeiyakuTime">
          </koyouKeiyakuTimeSelect>
        </div>


        <div class="item">
          <b class="title">振休取得区分</b>
          <select-box :options="kyuukaDiv" v-model="form.furikyuu.furikyuutani1_type" :hasBlank="true"
            :error="getError(form.furikyuu, 'furikyuutani1_type')"></select-box>
        </div>
      </template>

      <div class="item">
        <b class="title">備考</b>
        <input-textarea v-model="form.notes" :error="getError(form, 'notes')"></input-textarea>
      </div>
    </template>
  </div>
</template>

<script lang="ts">

import { defineComponent, computed, toRefs, watch, nextTick, ref, onMounted, Ref } from 'vue';
import { useFurikyuu } from "@/composable/shinsei/furikyuu/furikyuu";
import { useForm } from '@/composable/form/form';
import { useAuthStore } from '@/store/auth';
import OptionConst from '@/const/Option';
import { Shinsei, ShinseiKoyouKeiyakuTime } from '@/types/Shinsei';
import { KoyouKeiyakuTimeSelectEmitData, SyuugyouBasyoAndKoyouKeiyakuOption } from '@/types/SyuugyouBasyo';
import { cloneDeep } from 'lodash';
import { BaseOptionWithoutCode } from '@/types/Option';
import { Logger } from '@/utils/Logger';
import KoyouKeiyakuTimeSelect from "@/components/shinsei/create/parts/KoyouKeiyakuTimeSelect.vue"

export default defineComponent({
  props: {
    form: {
      type: Object as () => Shinsei,
      default: null
    }
  },
  emits: {
    changeKoyouKeiyakuTime: (selectedSyuugyouBasyo: SyuugyouBasyoAndKoyouKeiyakuOption | null, selectedKoyouKeiyakuTimeOption: ShinseiKoyouKeiyakuTime | null) => true,
  },
  setup(props, context) {
    const { form } = toRefs(props);
    const { getZanFurikyuuCount, zanFurikyuu: furikyuuCount } = useFurikyuu();
    const authStore = useAuthStore();

    const isFurikyuu = ref(0) // 実質booleanだがinput-radioに渡す都合上1 or 0としている
    const options: BaseOptionWithoutCode[] = [
      {
        val: 0,
        label: '振出'
      },
      {
        val: 1,
        label: '振休'
      },
    ]

    // 編集モードで使用する就業場所（雇用契約）と就業時間の初期値
    // 初期化時のみ使用し、以降は使用せずnullにする（実データはリクエストで送信される申請オブジェクト内に保持されるため）
    let workTimeId: Ref<number | null> = ref(null)
    let koyouKeiyakuId: Ref<number | null> = ref(null)

    const isInit = ref(false)

    onMounted(async () => {
      Logger.debug('----------------------------\n振休振出申請コンポーネントの初期化開始')
      Logger.debug('振休振出申請 onMounted', { '申請': form.value })
      nextTick(() => {
        Logger.debug('振休振出申請 onMounted->nextTick', { '申請': form.value })
        if (!form.value.furikyuu) {
          Logger.debug('新規作成モードの初期化を行います')
          form.value.furikyuu = {
            syukkin_date: null,
            syukkin_furidetani_type: null,
            start_time: null,
            end_time: null,
            kyuukei_time: null,
            furikyuu1_date: null,
            furikyuutani1_type: null,
            furikyuu2_date: null,
            furikyuutani2_type: null,
          }
        } else {
          if (form.value.furikyuu.syukkin_date) {
            isFurikyuu.value = 0 // 出勤日（振出日）が入力されている場合、振出申請とみなす
          } else if (form.value.furikyuu.furikyuu1_date || form.value.furikyuu.furikyuu2_date) {
            isFurikyuu.value = 1 // 振休日１or２が入力されている場合、振休申請とみなす
          }

          Logger.debug('編集モードの初期化を行います')
          workTimeId.value = form.value.shinsei_koyou_keiyaku_time.length > 0 ? form.value.shinsei_koyou_keiyaku_time[0].koyou_keiyaku_time_id : null
          koyouKeiyakuId.value = form.value.koyou_keiyaku_id ?? null
          Logger.debug('振休振出申請より就業場所（雇用契約）と就業時間を初期化しました', { '雇用契約ID': koyouKeiyakuId.value, '就業時間ID': workTimeId.value })
        }
        isInit.value = true
        Logger.debug('振休振出申請コンポーネントの初期化完了\n----------------------------')
      })
    })

    // エラー取得
    const { getError } = useForm();

    const minDate = computed(() => {
      if (!form.value.furikyuu) return
      if (!form.value.furikyuu.syukkin_date) return

      const minDate = cloneDeep(new Date(form.value.furikyuu.syukkin_date))
      minDate.setHours(0)
      minDate.setMinutes(0)
      minDate.setSeconds(0)
      minDate.setMilliseconds(0)
      return minDate
    })

    const maxDate = computed(() => {
      if (!form.value.furikyuu) return
      if (!form.value.furikyuu.syukkin_date) return

      const maxDate = cloneDeep(new Date(form.value.furikyuu.syukkin_date))
      maxDate.setHours(2)
      maxDate.setMinutes(0)
      maxDate.setSeconds(0)
      maxDate.setMilliseconds(0)
      return maxDate
    })

    const clear = () => {
      if (!form.value.furikyuu) return

      furikyuuCount.value = null

      form.value.furikyuu.syukkin_date = null
      form.value.furikyuu.syukkin_furidetani_type = null
      form.value.furikyuu.start_time = null
      form.value.furikyuu.end_time = null
      form.value.furikyuu.kyuukei_time = null
      form.value.furikyuu.furikyuu1_date = null
      form.value.furikyuu.furikyuutani1_type = null
      form.value.furikyuu.furikyuu2_date = null
      form.value.furikyuu.furikyuutani2_type = null
    }

    // 就業場所・就業時間が変更された場合に走る処理
    // 引数は以下コンポーネントのemits.changeに準拠
    // src/resources/ts/components/shinsei/create/parts/KoyouKeiyakuTimeSelect.vue
    const changeKoyouKeiyakuTime = (emitData: KoyouKeiyakuTimeSelectEmitData) => {
      if (!form.value.furikyuu) return
      if (!emitData.selectedSyuugyouBasyo) return

      koyouKeiyakuId.value = emitData.selectedSyuugyouBasyo?.koyou_id ?? null
      workTimeId.value = emitData.selectedKoyouKeiyakuTimeOption?.val ?? null

      // 就業場所に関する情報をemitする（申請オブジェクトの変更等は親に移譲する）
      context.emit('changeKoyouKeiyakuTime', emitData.selectedSyuugyouBasyo, null);
    }

    // 振休 / 振出の切替時、振休が選択されたタイミングで振休残数を取得する
    watch(isFurikyuu, async (newVal) => {
      if (newVal === 1) {
        const userId = form.value.user_id ?? authStore.user!.user_id
        await getZanFurikyuuCount(userId)
      }
    })
    return {
      getError,
      'furideDiv': OptionConst.SHINSEI_DIV_FURIDE_OPTION,
      'kyuukaDiv': OptionConst.SHINSEI_DIV_OPTION,
      maxDate,
      minDate,
      furikyuuCount,
      isFurikyuu,
      options,
      clear,
      workTimeId,
      koyouKeiyakuId,
      isInit,
      changeKoyouKeiyakuTime
    }
  },
  components: {
    'koyouKeiyakuTimeSelect': KoyouKeiyakuTimeSelect
  }
})

</script>
